'use client'

import { formatFontFamily } from '@/lib/themeHelpers'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'
import { Tv, UtensilsCrossed, Plane, Mail, Check, Circle } from 'lucide-react'

interface LifestyleInsight {
  icon: string;
  value: string;
  label: string;
  description: string;
}

interface AudienceSectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  content: any;
}

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  tv: Tv,
  utensils: UtensilsCrossed,
  plane: Plane,
  mail: Mail
};

// LifestyleInsights component for lifestyle insight cards
function LifestyleInsights({ theme, radius, insights }: { theme: any; radius: number; insights: LifestyleInsight[] }) {
  if (!insights || insights.length === 0) return null;

  return (
    <div
      className="p-5 transition-all duration-300 hover:-translate-y-1 cursor-default"
      style={{
        backgroundColor: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: radius
      }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
    >
      <h3 className="text-lg font-semibold mb-3 font-primary" style={{ color: theme.text }}>
        Audience Lifestyle Insights
      </h3>
      
      <div className="space-y-3">
        {insights.map((insight, index) => {
          const IconComponent = iconMap[insight.icon] || Tv;
          return (
            <div 
              key={index} 
              className="flex items-start gap-3 p-3 transition-all duration-200 hover:scale-[1.02] cursor-default" 
              style={{
                backgroundColor: theme.surface === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
                borderRadius: radius / 2
              }}
            >
              <div 
                className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #FF6B6B, #A55EEA)',
                }}
              >
                <IconComponent className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5 mb-0.5">
                  <span className="text-lg font-bold font-secondary" style={{ color: theme.text }}>
                    {insight.value}
                  </span>
                  <span className="text-xs font-semibold font-primary" style={{ color: theme.text }}>
                    {insight.label}
                  </span>
                </div>
                <p className="text-xs font-secondary" style={{ color: theme.mutedText }}>
                  {insight.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AudienceSection({
  surface, 
  theme, 
  radius, 
  fontFamily, 
  gradStart, 
  gradEnd, 
  angle,
  content
}: AudienceSectionProps) {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });

  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  return (
    <section id="audience" className="relative w-full" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 py-20 lg:px-8">
        {/* Header */}
        <div 
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 ${scrollAnimationClasses.transition} ${headerVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight font-primary" style={{ color: theme.text }}>
            {content.title}
          </h2>
          <p className="text-lg max-w-3xl mx-auto font-secondary" style={{ color: theme.mutedText }}>
            {content.description}
          </p>
        </div>


        {/* Charts Section */}
        <div 
          ref={contentRef as React.RefObject<HTMLDivElement>}
          className={`${scrollAnimationClasses.transition} ${contentVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          style={{ transitionDelay: '150ms' }}
        >
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Community Impact Card */}
            {content.brandStrengths && content.communityImpact && (
              <div
                className="p-5 transition-all duration-300 hover:-translate-y-1 cursor-default"
                style={{
                  backgroundColor: theme.card,
                  border: `1px solid ${theme.border}`,
                  borderRadius: radius
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                {/* Brand Strengths */}
                <h3 className="text-lg font-semibold mb-4 font-primary" style={{ color: theme.text }}>
                  {content.brandStrengths.title}
                </h3>
                <div className="space-y-1.5 mb-4">
                  {content.brandStrengths.items.map((item: string, index: number) => (
                    <div key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#1DD1A1' }} />
                      <span className="text-sm font-secondary" style={{ color: theme.text }}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Divider */}
                <div className="h-px my-4" style={{ backgroundColor: theme.border }} />

                {/* Community Impact */}
                <h3 className="text-lg font-semibold mb-4 font-primary" style={{ color: theme.text }}>
                  {content.communityImpact.title}
                </h3>
                <div className="space-y-1.5">
                  {content.communityImpact.items.map((item: string, index: number) => (
                    <div key={index} className="flex items-center gap-3">
                      <Circle className="w-2 h-2 flex-shrink-0 fill-current" style={{ color: '#A55EEA' }} />
                      <span className="text-sm font-secondary" style={{ color: theme.text }}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Lifestyle Insights Component */}
            <LifestyleInsights theme={theme} radius={radius} insights={content.lifestyleInsights} />
          </div>
        </div>

      </div>
    </section>
  );
}